import * as React from 'react'
import styled from 'styled-components'
import { useStore } from 'effector-react/ssr'
import { debounce } from 'debounce'
import aboutMeMobile320Layer1 from './mobile-bg-layer-1.png'
import aboutMeMobile320Layer2 from './mobile-bg-layer-2.png'
import aboutMeTablet768Layer1 from './tablet-bg-layer-1.png'
import aboutMeTablet768Layer2 from './tablet-bg-layer-2.png'
import { Social } from '@ui/social'
import { Devices } from '@styles'
import { $contacts } from '@pages/contacts/model'
import { matchMedia, MediaSource } from '@lib/image-media'
import { isClientSide } from '@lib/ssr'
import PlayIcon from './play.svg'
import { useMemo } from 'react'

const aboutMeMediaSource: MediaSource<string>[] = [
  { mediaQuery: Devices.tablet, source: aboutMeTablet768Layer1 },
  { mediaQuery: Devices.mobileL, source: aboutMeMobile320Layer1 },
  { mediaQuery: Devices.mobileM, source: aboutMeMobile320Layer1 },
  { mediaQuery: Devices.mobileS, source: aboutMeMobile320Layer1 },
]

export const AboutMeDesktop = () => {
  const contacts = useStore($contacts)
  const [images, setImages] = React.useState<{ aboutMeMobileCover: string }>({ aboutMeMobileCover: '' })

  const onResize = React.useCallback(
    debounce(() => {
      setImages({
        aboutMeMobileCover: matchMedia(aboutMeMediaSource, ''),
      })
    }, 100),
    [],
  )

  React.useEffect(() => {
    if (isClientSide()) {
      setImages({
        aboutMeMobileCover: matchMedia(aboutMeMediaSource, ''),
      })

      window.addEventListener('resize', onResize)
    }

    return () => {
      if (isClientSide()) {
        window.removeEventListener('resize', onResize)
      }
    }
  }, [])

  const backgroundImage = useMemo(() => {
    switch (images.aboutMeMobileCover) {
      case aboutMeMobile320Layer1:
        return `url(${aboutMeMobile320Layer2}), url(${images.aboutMeMobileCover})`
      case aboutMeTablet768Layer1:
        return `url(${aboutMeTablet768Layer2}), url(${images.aboutMeMobileCover})`
      default:
        return `url(${images.aboutMeMobileCover})`
    }
  }, [images.aboutMeMobileCover])

  return (
    <MobileCover
      style={{
        backgroundImage,
      }}
    >
      <Social />
      <PageWrapper>
        <Title>Обо мне</Title>

        <AboutMeTextWrapper>
          <p>Дорогие слушатели, приветствую на моем сайте!</p>

          <p>
            Я уверена, что вы — ищущие натуры, желающие по-своему постичь самую суть жизни во всем ее многообразии.
            <span style={{ display: 'block' }}>
              Вас не устраивают простые ответы на незаурядные вопросы, вы не ищете легких путей. В вашей жизни всегда
              есть место волшебству и хэппи-энду, романтическим комедиям. Но в то же время вы готовы принимать
              реальность без прикрас, учиться на ошибках, сопереживать драмам и трагедиям.
            </span>
          </p>

          <p>
            <span style={{ fontWeight: 'bold', color: '#333333' }}>Открою секрет:</span> именно вы и есть мои герои.
          </p>

          <p>
            Перевоплощения, погружение в разные образы и судьбы — об этом мои песни. Когда я их пишу, будто бы проживаю
            жизнь своих лирических героев и вместе с ними путешествую во времени.
          </p>

          <p>
            Многие мои истории еще не рассказаны и только зреют во мне. Большинство из них приходят ко мне в полете, на
            высоте 10 тысяч метров. А новые идеи возникают во время взлета и посадки. Именно в эти моменты очень резко
            меняется атмосферное давление, что пробуждает все органы чувств, заставляет их работать на полную мощность и
            наиболее остро чувствовать жизнь.
          </p>

          <p>
            Моя истина — в искренности и, конечно, в честном и открытом диалоге с вами. Я творю, чтобы по крупицам
            открывать великий замысел жизни и ценить ее все больше с каждым днем, любить мир во всем разнообразии,
            принимать радости и невзгоды с открытым сердцем. И обязательно верить… в себя, в лучшее, в людей вокруг.
          </p>

          <p>
            Но я готова принять и то, что вы совершенно другие. Быть может, все сказанное — совершенно не про вас. А
            какие вы? Пишите — мне всегда интересно узнать о вас больше.
          </p>
        </AboutMeTextWrapper>

        <Music href='https://band.link/chistova' target='_blank' rel='noopener'>
          <div style={{ marginRight: '15px' }}>
            <PlayIcon />
          </div>

          <span>СЛУШАТЬ МУЗЫКУ</span>
        </Music>

        <DownloadButton href={contacts?.file.url}>Скачать пресс-релиз</DownloadButton>
      </PageWrapper>
    </MobileCover>
  )
}

const MobileCover = styled.div`
  background-size: cover;
  background-position: top;
  background-repeat: no-repeat;
  padding-bottom: 20px;
  position: absolute;

  @media screen and ${Devices.laptop} {
    background-image: unset !important;
    background-size: unset;
    background-position: unset;
    background-repeat: unset;
    padding-bottom: unset;
    position: unset;
  }
`

const DownloadButton = styled.a`
  border: 1px solid white;
  box-sizing: border-box;
  background-color: transparent;
  border-radius: 68px;
  padding: 10px 35px;
  color: white;
  font-family: Raleway;
  font-size: 16px;
  line-height: 19px;
`

const Music = styled.a`
  display: flex;
  align-items: center;
  margin-bottom: 50px;
  font-size: 15px;
  line-height: 18px;
  text-decoration: none;
  color: black;

  @media screen and ${Devices.laptop} {
    margin-bottom: 23px;
  }
`

const PageWrapper = styled.div`
  --paddingLeft: 25px;
  --paddingRight: 25px;
  --paddingTop: 0px;
  --paddingBottom: 0px;
  --width: 100vw;
  margin: auto;

  padding-left: var(--paddingLeft);
  padding-right: var(--paddingRight);
  padding-bottom: var(--paddingBottom);
  width: var(--width);

  margin-top: 350px;
  margin-bottom: 50px;

  @media screen and ${Devices.mobileM} {
    margin-top: 400px;
  }

  @media screen and ${Devices.mobileL} {
    margin-top: 450px;
  }

  @media screen and ${Devices.tablet} {
    margin-top: 550px;
  }

  @media screen and ${Devices.laptop} {
    margin-top: unset;
    --paddingLeft: 160px;
    --paddingRight: 65px;
    --paddingTop: 0px;
    --paddingBottom: 0px;
    --width: 1200px;
  }

  @media screen and ${Devices.desktopS} {
    --width: 1366px;
  }

  @media screen and ${Devices.desktopM} {
    --width: 1900px;
  }
`

const Title = styled.h1`
  font-weight: normal;
  font-family: 'Magnolia Script';
  font-size: 30px;
  line-height: 42px;
  margin: 0;

  @media screen and ${Devices.laptop} {
    font-size: 80px;
    line-height: 113px;
    margin-bottom: 10px;
  }
`

const AboutMeTextWrapper = styled.div`
  font-family: Raleway;
  font-size: 15px;
  line-height: 18px;

  margin-bottom: 40px;

  @media screen and ${Devices.laptop} {
    font-size: 15px;
    line-height: 18px;
    width: 400px;
    margin-bottom: 0;
  }

  @media screen and ${Devices.desktopS} {
    width: 460px;
  }

  @media screen and ${Devices.desktopM} {
    width: 570px;
  }

  @media screen and ${Devices.desktopL} {
    width: 700px;
  }
`
